import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useFetcherGet } from '@/hooks/useFetcher';

interface placeholderSubstitute {
    type: 's' | 'd' | 'f',
    value: string|number
}

interface LocalizationProviderProps {
    language: string;
    texts: object;
    getText: (key: string, options?: Array<placeholderSubstitute>) => string;
    loading: boolean;
}

// Create the context
const LocalizationContext = createContext<LocalizationProviderProps>({
    language: '',
    texts: {},
    getText: (key: string, options?: Array<placeholderSubstitute>) => key,
    loading: true
});

// Create the LocalizationProvider component
const LocalizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [texts, setTexts] = useState<object>({});
    const [loading, setLoading] = useState<boolean>(true);
    // get language from browser
    const language = (document.querySelector('html')?.getAttribute('lang') || navigator.language || navigator.userLanguage).substr(0, 2);

    useEffect(() => {
        setLoading(true)

        useFetcherGet(`${import.meta.env.VITE_BACKEND_URL}/wp-json/garfy/v1/components/product-widget/i18n`, {lang: language}, 60 * 30).then((response) => {
            setTexts(response || {})
        }).catch((error) => {
            console.error(error)
        }).finally(() => {
            setLoading(false)
        })
    }, [])

    const getText = useMemo(() => (key: string, options: Array<placeholderSubstitute>) => {
        if (!(key in texts)) {
            console.debug(`Localization: Key ${key} not found in texts`, texts)
            return key;
        }
        
        let text = texts[key];

        // ciclo tutte le option e applico i placeholder, uno alla volta
        (options || []).forEach((option) => {
            text = text.replace(`%${option.type}`, option.value.toString());
        })

        return text;
    }, [texts])
    
    const contextStepsValue = useMemo(
        () => ({
            language,
            texts,
            getText,
            loading
        }),
        [texts, loading]
    )

    return (
        <LocalizationContext.Provider value={contextStepsValue}>
            {children}
        </LocalizationContext.Provider>
    );
};

// custom hook per accedere al contesto
export const useLocalization = () => { return useContext(LocalizationContext) }
export default LocalizationProvider
